import React from "react";
import { SbButton } from "uiCore";

// "Roles Impacted" totals are a count of DISTINCT roles, not a sum of the
// per-agent counts (the same role is usually impacted by several agents).
// Rows are agent-in-group memberships, so agents are de-duplicated first and
// rows with no role_ids fall back to their own count.
export const countUniqueRolesImpacted = (rows) => {
  const roleIds = new Set();
  const countedAgents = new Map();

  (rows || []).forEach((row) => {
    const ids = row?.role_ids;
    if (ids?.length) ids.forEach((id) => roleIds.add(id));
    else if (!countedAgents.has(row?.agent_id))
      countedAgents.set(row?.agent_id, parseInt(row?.roles_impacted, 10) || 0);
  });

  return (
    roleIds.size +
    [...countedAgents.values()].reduce((total, count) => total + count, 0)
  );
};

// Version cell: "-" for an agent the group offers but never selected, "v1" for a
// single version, "v1, v2" when it is selected in several versions of the group.
export const formatVersions = (record) => {
  const versions = record?.versions?.length
    ? record.versions
    : record?.version != null
      ? [record.version]
      : [];
  return versions.length ? versions.map((v) => `v${v}`).join(", ") : "-";
};

// Numeric column sorter; missing values sort as 0.
export const compareNumber = (key) => (a, b) =>
  Number(a?.[key] ?? 0) - Number(b?.[key] ?? 0);

// Compact currency, e.g. $1.82M.
export const formatPotentialValue = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(value || 0);

// Potential Value / ROI columns, shared by the agent table and the group
// drilldown so the two stay consistent. Both are gated on view_comp.
export const buildFinancialColumns = (canViewPotentialValue) => [
  {
    title: "Potential Value",
    dataIndex: "business_value",
    key: "business_value",
    align: "right",
    sorter: compareNumber("business_value"),
    sortDirections: ["ascend", "descend"],
    render: (value) =>
      canViewPotentialValue ? formatPotentialValue(value) : "-",
  },
  {
    title: "ROI",
    dataIndex: "roi",
    key: "roi",
    align: "right",
    sorter: compareNumber("roi"),
    sortDirections: ["ascend", "descend"],
    render: (value) => (canViewPotentialValue ? `${Number(value) || 0}` : "-"),
  },
];

// Group-level totals over de-duplicated agents. ROI mirrors the PAIC group page:
// SUM(business_value) / SUM(development_cost), never an average of per-agent ROI.
export const aggregateGroupTotals = (rows) => {
  const byAgent = new Map();
  (rows || []).forEach((row) => {
    if (!byAgent.has(row?.agent_id)) byAgent.set(row?.agent_id, row);
  });

  let businessValue = 0;
  let developmentCost = 0;
  let prdCount = 0;
  byAgent.forEach((agent) => {
    businessValue += parseFloat(agent?.business_value) || 0;
    developmentCost += parseFloat(agent?.development_cost) || 0;
    if (agent?.prd_generated) prdCount += 1;
  });

  // A missing or zero dev cost falls back to 1, the same guard the ROI pipeline
  // and the group page totals apply — otherwise the group reads ROI 0.
  const divisor = developmentCost || 1;

  return {
    business_value: businessValue,
    roi: Number((businessValue / divisor).toFixed(2)),
    prd_count: prdCount,
  };
};

// PRD column, shared by the agent table and the group drilldown. The row click
// also opens the PRD, so the link stops propagation to avoid a double navigate.
export const buildPrdColumn = (openPrd) => ({
  title: "PRD",
  dataIndex: "prd_generated",
  key: "prd_generated",
  align: "center",
  render: (ready, record) =>
    ready ? (
      <SbButton
        type="link"
        onClick={(e) => {
          e.stopPropagation();
          openPrd(record);
        }}
      >
        View
      </SbButton>
    ) : (
      "—"
    ),
});

export default countUniqueRolesImpacted;
